<template>
 <div
   class="flex-1 min-w-0 flex flex-col h-full bg-white"
   @dragover.prevent="handleDragOverChat"
   @dragleave="handleDragLeaveChat"
   @drop.prevent="handleDropChat"
 >

  <!-- Top bar -->
  <div class="flex items-center justify-between px-4 py-2 border-b border-gray-100 flex-shrink-0">
    <button
      @click="$emit('toggle-sidebar')"
      class="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
      title="사이드바 토글"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
      </svg>
    </button>
    <div class="flex items-center gap-1">
      <button
        class="px-2.5 py-1 rounded-md text-[12px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
        @click="isCommandExampleModalOpen = true"
      >명령어</button>
      <button
        class="px-2.5 py-1 rounded-md text-[12px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
        @click="openPromptModal"
      >프롬프트</button>
      <button
        class="px-2.5 py-1 rounded-md text-[12px] text-gray-400 hover:text-red-500 hover:bg-gray-50 transition-colors"
        @click="handleDeleteMessages"
        :disabled="isLoading"
      >초기화</button>
    </div>
  </div>

  <!-- Chat Area -->
  <div class="flex-1 min-h-0 relative">
    <ChatBox
      :chats="chats"
      ref="chatBoxRef"
      class="h-full"
    />

    <!-- Drop Zone -->
    <div
      v-if="isDraggingOverChat"
      class="absolute inset-4 bg-blue-50/80 backdrop-blur-sm border-2 border-dashed border-blue-300 rounded-xl flex items-center justify-center z-20 pointer-events-none"
    >
      <p class="text-sm text-blue-500 font-medium">파일을 여기에 놓으세요</p>
    </div>
  </div>

  <!-- Input Area -->
  <div class="flex-shrink-0 border-t border-gray-100">
    <div class="px-5 py-4">
      <form @submit.prevent="handleSubmit">
        <ChatInput
          :disabled="isLoading"
          placeholder="메시지 입력..."
          @send="onSendMessage"
          @trigger-upload="triggerFileSelect"
        />
      </form>
    </div>
  </div>

  <PromptModal ref="promptModalRef" />
  <CommandExampleModal v-model="isCommandExampleModalOpen" />
  <input ref="fileInput" type="file" class="hidden" :accept="acceptedFileTypes" @change="onFileSelected" />
 </div>
</template>

<script setup>
import { onMounted, ref, nextTick } from 'vue'
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ChatBox from '@/components/ChatBox.vue';
import ChatInput from './ChatInputNew.vue';
import PromptModal from './PromptModal.vue'
import CommandExampleModal from './CommandExampleModal.vue'

const store = useStore();
const emit = defineEmits(['file-uploaded', 'toggle-sidebar']);
const route = useRoute()

const props = defineProps({
  modelValue: { type: Array, default: () => ([]) },
  selectedJobIds: { type: Array, default: () => ([]) },
  selectedFileIds: { type: Array, default: () => ([]) },
})

const isLoading = ref(false);
const promptModalRef = ref(null);
const isCommandExampleModalOpen = ref(false);
const chatBoxRef = ref(null);
const uploadedFiles = ref([]);
const fileInput = ref(null);
const acceptedFileTypes = ".xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf";
const isDraggingOverChat = ref(false);

const scrollToBottom = () => {
  nextTick(() => {
    if (chatBoxRef.value?.$el) {
      const el = chatBoxRef.value.$el;
      el.scrollTop = el.scrollHeight;
    }
  });
};

const chats = ref([])

async function handleSubmit(msgParam) {
  if (!msgParam.trim()) return;
  if (msgParam.startsWith('/워드클라우드')) {
    if (msgParam.split(' ')[0] !== '/워드클라우드') {
      store.commit('handleNotify', { message: '잘못된 명령어 형식입니다.', type: 'error' });
      return;
    }
  }
  try {
    isLoading.value = true;
    chats.value.push({ text: msgParam, type: 'user' });
    const loadingText = msgParam.startsWith('/') ? '명령어 처리중' : 'AI 답변 생성중';
    chats.value.push({ text: loadingText, type: 'loading' });
    scrollToBottom();
    await store.dispatch('chat/sendMessage', {
      content: msgParam,
      crawlingJobIds: props.selectedJobIds,
      fileIds: props.selectedFileIds
    });
    // 로딩 메시지 제거
    const loadingIdx = chats.value.findLastIndex(c => c.type === 'loading');
    if (loadingIdx !== -1) chats.value.splice(loadingIdx, 1);
    const lastMessage = store.state.chat.messages[store.state.chat.messages.length - 1];
    if (lastMessage && lastMessage.content) {
      let messageContent = lastMessage.content;
      try {
        if (typeof messageContent === 'string' &&
            (messageContent.includes('"chartData"') || messageContent.includes('chartData'))) {
          const parsedContent = JSON.parse(messageContent);
          if (parsedContent && parsedContent.chartData) messageContent = parsedContent;
        }
      } catch (e) { console.error('Error parsing message content:', e); }

      // 텍스트인 경우 스트리밍 효과로 표시
      const textForStream = typeof messageContent === 'string'
        ? messageContent
        : (messageContent?.Response && typeof messageContent.Response === 'string' && !messageContent?.chartData)
          ? messageContent.Response
          : null;

      if (textForStream) {
        await streamText(textForStream, lastMessage.role);
      } else {
        chats.value.push({ text: messageContent, type: lastMessage.role });
        scrollToBottom();
      }
    }
  } catch (error) {
    console.error('Failed to send message:', error);
  } finally {
    isLoading.value = false;
  }
}

async function getPrevChat() {
  try {
    await store.dispatch('chat/initializeChat', {
      selectedJobs: props.modelValue[0],
      sessionId: route.query.historyId,
    });
    chats.value = store.state.chat.messages
      .filter((msg) => msg.content)
      .map((msg) => ({ text: msg.content, type: msg.role, timestamp: new Date(msg.created_at) }))
      .sort((a, b) => a.timestamp - b.timestamp);
    scrollToBottom();
  } catch (err) { console.error('Failed to initialize chat:', err); }
}

const openPromptModal = () => { promptModalRef.value?.open(); };

async function handleDeleteMessages() {
  if (!confirm('모든 대화 내용을 삭제하시겠습니까?')) return;
  try {
    isLoading.value = true;
    await store.dispatch('chat/deleteAllMessages');
    chats.value = [];
  } catch (error) { console.error('Failed to delete messages:', error); }
  finally { isLoading.value = false; }
}

function streamText(fullText, role) {
  return new Promise((resolve) => {
    const chatEntry = { text: '', type: role, streaming: true };
    chats.value.push(chatEntry);
    const idx = chats.value.length - 1;
    let pos = 0;
    const speed = Math.max(5, Math.min(30, 3000 / fullText.length));
    const tick = () => {
      // 한 번에 1~3글자씩 추가
      const chunk = Math.min(pos + Math.ceil(Math.random() * 3), fullText.length);
      chats.value[idx] = { ...chats.value[idx], text: fullText.slice(0, chunk) };
      pos = chunk;
      scrollToBottom();
      if (pos < fullText.length) {
        setTimeout(tick, speed);
      } else {
        chats.value[idx] = { ...chats.value[idx], streaming: false };
        resolve();
      }
    };
    tick();
  });
}

function onSendMessage(message) { handleSubmit(message); }
function triggerFileSelect() { fileInput.value.click(); }
function onFileSelected(event) {
  const file = event.target.files[0];
  if (!file) return;
  processFile(file);
  event.target.value = '';
}

function processFile(file) {
  const allowedExtensions = ['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'pdf'];
  const maxSizeInBytes = 100 * 1024 * 1024;
  const ext = file.name.split('.').pop().toLowerCase();
  if (!allowedExtensions.includes(ext)) { alert("허용되지 않은 파일 형식입니다."); return; }
  if (file.size > maxSizeInBytes) { alert("파일 용량은 최대 100MB입니다."); return; }
  const sessionId = store.state.chat.session?.id || route.query.historyId;
  if (!sessionId) { alert("세션 정보가 없습니다."); return; }
  const uploading = store.getters['file/getUploadingFilesBySession'](sessionId).length;
  if (uploadedFiles.value.length + uploading >= 5) { alert("최대 5개까지 업로드 가능합니다."); return; }
  const formData = new FormData();
  formData.append("file", file);
  formData.append("session_id", sessionId);
  store.dispatch("file/uploadFile", { formData, sessionId })
    .then(res => { if (res.data.success) emit('file-uploaded'); else alert(res.data.message || "업로드 실패"); })
    .catch(() => alert("파일 업로드 중 오류가 발생했습니다."));
}

const handleDragOverChat = (e) => {
  if (e.dataTransfer.items?.[0]?.kind === 'file') isDraggingOverChat.value = true;
}
const handleDragLeaveChat = () => { isDraggingOverChat.value = false; }
const handleDropChat = (e) => {
  isDraggingOverChat.value = false;
  if (e.dataTransfer.files.length > 0) processFile(e.dataTransfer.files[0]);
}

onMounted(() => { if (route.query.historyId) getPrevChat(); })
</script>
